import { useState, useRef, useEffect, useContext, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import useMutation from "../../../../shared/components/hooks/useMutation";
import useQuery from "../../../../shared/components/hooks/useQuery";
import { PropertyContext } from "../../../../contexts/PropertyContext";
import { Message } from "../../../../shared/types/message";
import { getLocalStorageString } from "../../../../shared/utils/localStorage";
import { PreviousChat, PropertyOption } from "../../../../shared/types/chatWindow.types";
import { getTimeAgo, parseMentions } from "../../../../shared/utils/chatbotUtils";
import { useAudioPlayer } from "../../../../shared/components/hooks/useAudioPlayer";

type BotStatus = "idle" | "thinking" | "typing";

const useChatbotLogic = () => {
    const customerID = localStorage.getItem("customerID")?.replace(/"/g, "");
    const storedName = getLocalStorageString("name");
    const greetingName = storedName ? ` ${storedName}` : "";

    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        {
            id: uuidv4(),
            text: `Hi${greetingName}, Ask me about your hotel performance`,
            isBot: true,
            timestamp: new Date(),
        },
    ]);
    const [input, setInput] = useState("");
    const [status, setStatus] = useState<BotStatus>("idle");

    const [liveThinkingMessage, setLiveThinkingMessage] = useState("Connecting to assistant...");
    const thinkingStages = [liveThinkingMessage];
    const [currentStage, setCurrentStage] = useState<number | null>(null);

    const [showMentions, setShowMentions] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    const [selectedMentions, setSelectedMentions] = useState<PropertyOption[]>([]);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const eventSourceRef = useRef<EventSource | null>(null);
    const typingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const threadIdRef = useRef<string>(uuidv4());
    const inputRef = useRef<HTMLInputElement>(null);

    const { propertyCode, propertyName, pmsList } = useContext(PropertyContext);
    const { modifyData } = useMutation();

    const [previousChats, setPreviousChats] = useState<PreviousChat[]>([]);
    const [cursor, setCursor] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const limit = 20;

    const cursorParam = cursor ? `&before_timestamp=${encodeURIComponent(cursor)}` : "";
    const conversationsUrl = `${process.env.REACT_APP_API_URL}/intelligence/conversations?customer_id=${customerID}&limit=${limit}${cursorParam}`;

    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);

    const startRecording = async () => {
        if (isRecording || isTranscribing) return;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            audioChunksRef.current = [];

            const mimeType = MediaRecorder.isTypeSupported("audio/webm")
                ? "audio/webm"
                : MediaRecorder.isTypeSupported("audio/mp4")
                    ? "audio/mp4"
                    : "";

            const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                const finalMime = mimeType || "audio/webm";
                const audioBlob = new Blob(audioChunksRef.current, { type: finalMime });
                const fileExtension = finalMime.includes("mp4") ? "mp4" : "webm";

                stream.getTracks().forEach((track) => track.stop());

                await sendAudioForTranscription(audioBlob, `audio.${fileExtension}`);
            };

            mediaRecorder.start();
            setIsRecording(true);
        } catch (err) {
            console.error("Microphone access error or denied:", err);
            setIsRecording(false);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const sendAudioForTranscription = async (blob: Blob, filename: string) => {
        setIsTranscribing(true);
        try {
            const formData = new FormData();
            formData.append("file", blob, filename);

            const res = await modifyData(
                `${process.env.REACT_APP_API_URL}/intelligence/audio/transcribe`,
                "post",
                formData
            );

            const text = res?.text?.trim();

            if (text) {
                setInput((prev) => (prev ? `${prev.trim()} ${text}` : text));
            } else {
                setMessages((prev) => [
                    ...prev,
                    {
                        id: uuidv4(),
                        text: "No speech was detected. Please try recording again.",
                        isBot: true,
                        timestamp: new Date(),
                    },
                ]);
            }
        } catch (err: any) {
            console.error("Transcription mutation error:", err);
            setMessages((prev) => [
                ...prev,
                {
                    id: uuidv4(),
                    text: "Failed to transcribe audio. Please check your microphone and try again.",
                    isBot: true,
                    timestamp: new Date(),
                },
            ]);
        } finally {
            setIsTranscribing(false);
        }
    };

    const { activeMessageId, playbackState, playAudio, stopAudio } = useAudioPlayer();

    const {
        data: conversationsData,
        loading: isLoadingChats,
        fetchData: refetchConversations,
    } = useQuery(
        conversationsUrl,
        undefined,
        null,
        { enabled: isOpen && !!customerID && hasMore }
    );

    useEffect(() => {
        if (conversationsData && Array.isArray(conversationsData)) {
            if ((conversationsData as any).length < limit) {
                setHasMore(false);
            }

            const formattedNewChats: PreviousChat[] = (conversationsData as any).map((chat: any) => ({
                id: chat.thread_id,
                title: chat.title,
                timeAgo: getTimeAgo(chat.updated_at),
                updatedAt: chat.updated_at,
            }));

            setPreviousChats((prev) => {
                const existingIds = new Set(prev.map((c) => c.id));
                const uniqueNew = formattedNewChats.filter((c) => !existingIds.has(c.id));
                return [...prev, ...uniqueNew];
            });
        }
    }, [conversationsData, limit]);

    const loadMoreChats = () => {
        if (!isLoadingChats && hasMore && previousChats.length > 0) {
            const lastChat = previousChats[previousChats.length - 1];
            if (lastChat?.updatedAt) {
                setCursor(lastChat.updatedAt);
            }
        }
    };

    const [activeHistoryThreadId, setActiveHistoryThreadId] = useState<string | null>(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
    const [messageCursor, setMessageCursor] = useState<string | null>(null);
    const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(true);

    const isPaginatingMessagesRef = useRef<boolean>(false);
    const prevScrollHeightRef = useRef<number>(0);

    const messageLimit = 20;
    const messageCursorParam = messageCursor ? `&before_timestamp=${encodeURIComponent(messageCursor)}` : "";

    const {
        data: historyData,
        loading: isFetchingHistory,
        error: historyError,
    } = useQuery(
        activeHistoryThreadId
            ? `${process.env.REACT_APP_API_URL}/intelligence/conversations/${activeHistoryThreadId}/messages?limit=${messageLimit}${messageCursorParam}`
            : "",
        undefined,
        null,
        { enabled: !!activeHistoryThreadId && hasMoreMessages }
    );

    const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
                top: scrollContainerRef.current.scrollHeight,
                behavior,
            });
        }
    }, []);

    useEffect(() => {
        if (isFetchingHistory && isPaginatingMessagesRef.current && scrollContainerRef.current) {
            prevScrollHeightRef.current = scrollContainerRef.current.scrollHeight;
        }
    }, [isFetchingHistory]);

    useEffect(() => {
        if (
            activeHistoryThreadId &&
            historyData &&
            (historyData as any).thread_id === activeHistoryThreadId &&
            Array.isArray((historyData as any).messages)
        ) {
            const rawMessages = (historyData as any).messages;

            if (rawMessages.length < messageLimit) {
                setHasMoreMessages(false);
            }

            const fetchedHistory: Message[] = rawMessages.map((msg: any) => ({
                id: msg.id ? msg.id.toString() : uuidv4(),
                text: msg.content || "",
                isBot: msg.role === "ai",
                timestamp: msg.created_at ? new Date(msg.created_at) : new Date(),
                threadId: activeHistoryThreadId,
                rawId: msg.id || 0,
            }));

            const sortedHistory = fetchedHistory.sort((a: any, b: any) => {
                const timeDiff = a.timestamp.getTime() - b.timestamp.getTime();
                if (timeDiff === 0) return (a.rawId || 0) - (b.rawId || 0);
                return timeDiff;
            });

            if (isPaginatingMessagesRef.current) {
                setMessages((prev) => [...sortedHistory, ...prev]);

                requestAnimationFrame(() => {
                    if (scrollContainerRef.current) {
                        const newScrollHeight = scrollContainerRef.current.scrollHeight;
                        scrollContainerRef.current.scrollTop = newScrollHeight - prevScrollHeightRef.current;
                    }
                    isPaginatingMessagesRef.current = false;
                });
            } else {
                setMessages(sortedHistory);
                setIsLoadingHistory(false);

                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        scrollToBottom("auto");
                    });
                });
            }
        }
    }, [historyData, activeHistoryThreadId, messageLimit, scrollToBottom]);

    useEffect(() => {
        if (activeHistoryThreadId && historyError) {
            setMessages([
                {
                    id: uuidv4(),
                    text: "Sorry, we couldn't load this conversation.",
                    isBot: true,
                    timestamp: new Date(),
                },
            ]);
            setActiveHistoryThreadId(null);
            setIsLoadingHistory(false);
            isPaginatingMessagesRef.current = false;
        }
    }, [historyError, activeHistoryThreadId]);

    const triggerFetchOlderMessages = useCallback(() => {
        if (!isFetchingHistory && hasMoreMessages && messages.length > 0 && !isLoadingHistory) {
            const oldestMessage = messages[0];
            if (oldestMessage?.timestamp) {
                isPaginatingMessagesRef.current = true;
                const isoString = oldestMessage.timestamp.toISOString().replace("Z", "");
                setMessageCursor(isoString);
            }
        }
    }, [isFetchingHistory, hasMoreMessages, messages, isLoadingHistory]);

    const handleScrollMessages = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        if (target.scrollTop <= 40) {
            triggerFetchOlderMessages();
        }
    };

    const handleWheelMessages = (e: React.WheelEvent<HTMLDivElement>) => {
        if (e.deltaY < 0 && e.currentTarget.scrollTop <= 10) {
            triggerFetchOlderMessages();
        }
    };

    useEffect(() => {
        if (!isPaginatingMessagesRef.current && !isLoadingHistory) {
            scrollToBottom(status === "typing" ? "auto" : "smooth");
        }
    }, [messages, status, isLoadingHistory, scrollToBottom]);

    const {
        data: propertiesData,
        loading: isLoadingProps
    } = useQuery(
        `${process.env.REACT_APP_API_URL}/properties/my`,
        {},
        null,
        { enabled: isOpen }
    );

    useEffect(() => {
        if (!propertyCode || !propertyName) return;
        setSelectedMentions([{ code: propertyCode, name: propertyName }]);
    }, [propertyCode, propertyName]);

    const rawItems = (propertiesData as any)?.items;
    const propertiesList: PropertyOption[] = Array.isArray(rawItems)
        ? rawItems.map((item: any) => ({
            name: item.property.name,
            code: item.property.code,
            pmsList: item.property.pmsList || [],
        }))
        : [];

    const propertiesWithAll: PropertyOption[] = [
        { name: "all", code: "all" },
        ...propertiesList,
    ];

    const filteredProperties = propertiesWithAll.filter((p) => {
        const query = mentionQuery.toLowerCase();
        return p.name.toLowerCase().includes(query) || p.code.toLowerCase().includes(query);
    });

    const handleStop = useCallback(() => {
        if (abortControllerRef.current) abortControllerRef.current.abort();

        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
        }

        setStatus("idle");
        setCurrentStage(null);
    }, []);

    const simulateTyping = (messageId: string, fullText: string, botMessageTemplate: Message) => {
        setStatus("typing");
        setCurrentStage(null);

        setMessages((prev) => {
            if (!prev.some((m) => m.id === messageId)) {
                return [...prev, { ...botMessageTemplate, text: "" }];
            }
            return prev;
        });

        let i = 0;
        if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);

        typingIntervalRef.current = setInterval(() => {
            setMessages((prev) => {
                const updated = [...prev];
                const idx = updated.findIndex((m) => m.id === messageId);
                if (idx !== -1) {
                    updated[idx] = { ...updated[idx], text: fullText.slice(0, i + 2) };
                }
                return updated;
            });
            i += 2;

            if (i >= fullText.length) {
                if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);
                typingIntervalRef.current = null;
                setStatus("idle");
            }
        }, 10);
    };

    const fetchBotResponse = async (userMessage: string, mentions: PropertyOption[]) => {
        setLiveThinkingMessage("Connecting to assistant...");
        setStatus("thinking");
        setCurrentStage(0);
        abortControllerRef.current = new AbortController();

        const selectedPropertiesPayload =
            mentions.length === 1 && mentions[0].code === "all"
                ? ["all:all"]
                : mentions.map((p) => `${p.code}:${p.name}`);

        let aggregatedPmsList: any[] = [];

        if (mentions.length === 1 && mentions[0].code === "all") {
            aggregatedPmsList = pmsList || [];
        } else {
            const collectedPms: any[] = [];
            mentions.forEach((mention) => {
                if (mention.pmsList && mention.pmsList.length > 0) {
                    collectedPms.push(...mention.pmsList);
                } else if (mention.code === propertyCode) {
                    const storedPms = localStorage.getItem("selectedPmsList");
                    const contextPms = pmsList.length > 0 ? pmsList : storedPms ? JSON.parse(storedPms) : [];
                    collectedPms.push(...contextPms);
                }
            });

            aggregatedPmsList = Array.from(
                new Map(collectedPms.map((item) => [item.id, item])).values()
            );
        }

        if (aggregatedPmsList.length === 0) {
            const backup = localStorage.getItem("selectedPmsList");
            if (backup) aggregatedPmsList = JSON.parse(backup);
        }

        const propertiesToSend =
            selectedPropertiesPayload.length > 0
                ? { selected_properties: selectedPropertiesPayload }
                : {};

        try {
            setLiveThinkingMessage("Initializing...");
            const postResponse = await modifyData(
                `${process.env.REACT_APP_API_URL}/intelligence/chat/stream-start`,
                "post",
                {
                    messages: userMessage,
                    thread_id: threadIdRef.current,
                    debug: false,
                    customer_id: customerID,
                    pms_list: aggregatedPmsList,
                    ...propertiesToSend,
                },
                false,
                abortControllerRef.current.signal
            );

            const actualThreadId = postResponse?.thread_id || threadIdRef.current;
            threadIdRef.current = actualThreadId;

            setPreviousChats((prev) => {
                const existingIdx = prev.findIndex((c) => c.id === actualThreadId);
                if (existingIdx !== -1) {
                    const updated = [...prev];
                    const [chat] = updated.splice(existingIdx, 1);
                    return [{ ...chat, timeAgo: "just now", updatedAt: new Date().toISOString() }, ...updated];
                } else {
                    const optimisticTitle =
                        userMessage.length > 40 ? userMessage.slice(0, 40) + "..." : userMessage;

                    return [
                        {
                            id: actualThreadId,
                            title: optimisticTitle,
                            timeAgo: "just now",
                            updatedAt: new Date().toISOString(),
                        },
                        ...prev,
                    ];
                }
            });

            const botMessageId = uuidv4();
            const botMessage: Message = {
                id: botMessageId,
                text: "",
                isBot: true,
                timestamp: new Date(),
                threadId: actualThreadId,
            };

            if (postResponse?.from_cache === true) {
                const textToType = postResponse.response || postResponse.text || "Cache hit, but no text found.";
                simulateTyping(botMessageId, textToType, botMessage);
                return;
            }

            if (!postResponse?.job_id) {
                const textToType = postResponse?.response || postResponse?.text || "Error: No job_id returned.";
                simulateTyping(botMessageId, textToType, botMessage);
                return;
            }

            let accumulatedText = "";
            let hasStartedStreaming = false;

            const sseUrl = `${process.env.REACT_APP_API_URL}/intelligence/chat/stream/${postResponse.job_id}`;
            const es = new EventSource(sseUrl);
            eventSourceRef.current = es;

            const closeStream = (forceIdle = true) => {
                es.close();
                eventSourceRef.current = null;
                if (forceIdle) {
                    setStatus("idle");
                    setCurrentStage(null);
                }
                refetchConversations();
            };

            es.addEventListener("close", () => {
                closeStream(true);
            });

            es.addEventListener("message", (event: any) => {
                if (event.data === "[DONE]") {
                    closeStream(true);
                    return;
                }

                try {
                    const data = JSON.parse(event.data);

                    if (data.status && data.status !== "done" && data.message) {
                        if (!hasStartedStreaming) {
                            setLiveThinkingMessage(data.message);
                        }
                    }

                    const liveToken = data.token !== undefined ? data.token : data.content;

                    if (liveToken !== undefined) {
                        if (liveToken.length > 30 && !hasStartedStreaming) {
                        } else {
                            if (!hasStartedStreaming) {
                                hasStartedStreaming = true;
                                setStatus("typing");
                                setCurrentStage(null);
                                setMessages((prev) => [...prev, botMessage]);
                            }

                            let tokenText = liveToken;
                            try {
                                tokenText = JSON.parse(tokenText);
                            } catch (e) { }
                            accumulatedText += tokenText;

                            setMessages((prev) => {
                                const updated = [...prev];
                                const idx = updated.findIndex((m) => m.id === botMessageId);
                                if (idx !== -1) updated[idx] = { ...updated[idx], text: accumulatedText };
                                return updated;
                            });
                        }
                    }

                    const finalPayload = data.result || data;

                    if (data.status === "done" || finalPayload.response !== undefined) {
                        let finalText = finalPayload.response || accumulatedText;

                        if (!finalText || finalText.trim() === "") {
                            finalText = "The agent failed to generate a response.";
                        }

                        if (!hasStartedStreaming) {
                            simulateTyping(botMessageId, finalText, botMessage);
                            closeStream(false);
                        } else {
                            setMessages((prev) => {
                                const updated = [...prev];
                                const idx = updated.findIndex((m) => m.id === botMessageId);
                                if (idx !== -1) updated[idx] = { ...updated[idx], text: finalText };
                                return updated;
                            });
                            closeStream(true);
                        }
                    }
                } catch (e) {
                    if (typeof event.data === "string" && !event.data.includes("{")) {
                        if (!hasStartedStreaming) {
                            hasStartedStreaming = true;
                            setStatus("typing");
                            setCurrentStage(null);
                            setMessages((prev) => [...prev, botMessage]);
                        }
                        accumulatedText += event.data;
                        setMessages((prev) => {
                            const updated = [...prev];
                            const idx = updated.findIndex((m) => m.id === botMessageId);
                            if (idx !== -1) updated[idx] = { ...updated[idx], text: accumulatedText };
                            return updated;
                        });
                    }
                }
            });

            es.onerror = (err) => {
                console.error("Chat SSE Error:", err);
                if (accumulatedText.length === 0) {
                    if (!hasStartedStreaming) {
                        setMessages((prev) => [...prev, { ...botMessage, text: "Error connecting to AI stream." }]);
                    } else {
                        setMessages((prev) => {
                            const updated = [...prev];
                            const idx = updated.findIndex((m) => m.id === botMessageId);
                            if (idx !== -1) updated[idx] = { ...updated[idx], text: "Error connecting to AI stream." };
                            return updated;
                        });
                    }
                }
                closeStream(true);
            };
        } catch (error: any) {
            if (!axios.isCancel(error)) {
                setMessages((prev) => [
                    ...prev,
                    {
                        id: uuidv4(),
                        text: "Response has been stopped or failed.",
                        isBot: true,
                        timestamp: new Date(),
                    },
                ]);
            }
            setStatus("idle");
            setCurrentStage(null);
        } finally {
            abortControllerRef.current = null;
        }
    };

    const handleSend = () => {
        if (!input.trim() || status !== "idle") return;

        const userMessage: Message = {
            id: uuidv4(),
            text: input,
            isBot: false,
            timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage]);

        const allPropsMaster = propertiesWithAll;
        const newMentionsFromInput = parseMentions(input, allPropsMaster);

        let propertiesToUse: PropertyOption[];

        if (newMentionsFromInput.length > 0) {
            propertiesToUse = newMentionsFromInput;
            setSelectedMentions(newMentionsFromInput);
        } else if (selectedMentions.length > 0) {
            propertiesToUse = selectedMentions;
        } else if (propertyCode && propertyName) {
            const storedPms = localStorage.getItem("selectedPmsList");
            const currentPms = pmsList.length > 0 ? pmsList : storedPms ? JSON.parse(storedPms) : [];
            const matchingMasterProp = propertiesList.find((p) => p.code === propertyCode);
            const defaultProp: PropertyOption = {
                code: propertyCode,
                name: propertyName,
                pmsList: matchingMasterProp?.pmsList || currentPms,
            };
            propertiesToUse = [defaultProp];
            setSelectedMentions([defaultProp]);
        } else {
            propertiesToUse = [];
        }

        fetchBotResponse(input, propertiesToUse);
        setInput("");
    };

    const handleSelectPreviousChat = (threadId: string) => {
        if (status !== "idle") return;

        if (messages.length > 1 && threadIdRef.current !== threadId) {
            refetchConversations();
        }

        threadIdRef.current = threadId;
        setIsLoadingHistory(true);
        setMessages([]);

        isPaginatingMessagesRef.current = false;
        setMessageCursor(null);
        setHasMoreMessages(true);
        setActiveHistoryThreadId(threadId);
    };

    const handleSelectProperty = (property: PropertyOption) => {
        const isAll = property.code === "all";
        const newValue = input.replace(/@([^\s]*)$/, `@${property.name} `);
        setInput(newValue);

        setSelectedMentions(() => {
            if (isAll) return [{ name: "all", code: "all" }];
            const mentionedInText = parseMentions(newValue, propertiesWithAll.filter((p) => p.code !== "all"));
            if (!mentionedInText.some((m) => m.code === property.code)) {
                mentionedInText.push(property);
            }
            return mentionedInText;
        });

        setShowMentions(false);
        inputRef.current?.focus();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const val = e.target.value;
        setInput(val);

        const mentionMatch = val.match(/@([^\s]*)$/);
        if (mentionMatch) {
            const query = mentionMatch[1];
            setMentionQuery(query);
            setShowMentions(true);
        } else {
            setShowMentions(false);
        }
    };

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) abortControllerRef.current.abort();
            if (eventSourceRef.current) eventSourceRef.current.close();
            if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);
        };
    }, []);

    const handleNewChat = () => {
        if (status !== "idle") {
            handleStop();
        }

        if (messages.length > 1) {
            refetchConversations();
        }

        threadIdRef.current = uuidv4();
        setActiveHistoryThreadId(null);
        setIsLoadingHistory(false);
        setMessageCursor(null);
        setHasMoreMessages(true);
        isPaginatingMessagesRef.current = false;

        setMessages([
            {
                id: uuidv4(),
                text: `Hi${greetingName}, Ask me about your hotel performance`,
                isBot: true,
                timestamp: new Date(),
            },
        ]);

        setInput("");
    };

    const deleteConversation = async (threadId: string) => {
        setPreviousChats((prev) => prev.filter((chat) => chat.id !== threadId));

        if (threadIdRef.current === threadId || activeHistoryThreadId === threadId) {
            handleNewChat();
        }

        try {
            await modifyData(
                `${process.env.REACT_APP_API_URL}/intelligence/conversations/${threadId}`,
                "delete"
            );
        } catch (error) {
            console.error("Failed to delete conversation:", error);
            refetchConversations();
        }
    };

    return {
        isOpen,
        setIsOpen,
        messages,
        input,
        status,
        currentStage,
        showMentions,
        filteredProperties,
        isLoadingProps,
        inputRef,
        messagesEndRef,
        scrollContainerRef,
        handleInputChange,
        handleSelectProperty,
        handleSend,
        handleStop,
        thinkingStages,
        previousChats,
        handleSelectPreviousChat,
        handleNewChat,
        loadMoreChats,
        isFetchingMore: isLoadingChats,
        hasMore,
        isLoadingHistory,
        deleteConversation,
        handleScrollMessages,
        handleWheelMessages,
        isFetchingOlderMessages: isFetchingHistory && isPaginatingMessagesRef.current,
        hasMoreMessages,
        isRecording,
        isTranscribing,
        startRecording,
        stopRecording,
        activeAudioId: activeMessageId,
        playbackState,
        onTogglePlayAudio: playAudio,
        stopAudio,
    };
};

export default useChatbotLogic;